import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = ['sourceChart', 'distributionChart', 'trendChart']

  static values = {
    url: String
  }

  charts = {}

  connect() {
    this.loadData()
  }

  disconnect() {
    Object.values(this.charts).forEach((chart) => chart.destroy())
    this.charts = {}
  }

  async loadData() {
    try {
      const response = await fetch(this.urlValue, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.renderCharts(data)
    } catch (error) {
      console.error('Failed to load usage data:', error)
    }
  }

  renderCharts(data) {
    if (this.hasSourceChartTarget && data.by_source) {
      this.renderSourceChart(data.by_source)
    }

    if (this.hasDistributionChartTarget && data.tests_per_user) {
      this.renderDistributionChart(data.tests_per_user)
    }

    if (this.hasTrendChartTarget && data.daily_trend) {
      this.renderTrendChart(data.daily_trend)
    }
  }

  renderSourceChart(bySource) {
    const canvas = document.createElement('canvas')
    this.sourceChartTarget.innerHTML = ''
    this.sourceChartTarget.appendChild(canvas)

    const labels = Object.keys(bySource).map((s) => this.formatSourceLabel(s))
    const values = Object.values(bySource)
    const total = values.reduce((a, b) => a + b, 0)

    this.charts.source = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'],
            borderWidth: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 8,
              font: { size: 10 }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.raw
                const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                return `${context.label}: ${value} (${percent}%)`
              }
            }
          }
        }
      }
    })
  }

  renderDistributionChart(distribution) {
    const canvas = document.createElement('canvas')
    this.distributionChartTarget.innerHTML = ''
    this.distributionChartTarget.appendChild(canvas)

    const labels = Object.keys(distribution)
    const values = Object.values(distribution)

    this.charts.distribution = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Users',
            data: values,
            backgroundColor: '#6366F1',
            borderWidth: 0,
            borderRadius: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 10 } }
          },
          y: {
            beginAtZero: true,
            grid: { color: '#E2E8F0' },
            ticks: { font: { size: 10 } }
          }
        }
      }
    })
  }

  renderTrendChart(dailyTrend) {
    const canvas = document.createElement('canvas')
    this.trendChartTarget.innerHTML = ''
    this.trendChartTarget.appendChild(canvas)

    const labels = dailyTrend.map((d) => this.formatDate(d.date))
    const values = dailyTrend.map((d) => d.count)

    this.charts.trend = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Uploads',
            data: values,
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { size: 9 },
              maxRotation: 0,
              maxTicksLimit: 7
            }
          },
          y: {
            beginAtZero: true,
            grid: { color: '#E2E8F0' },
            ticks: { font: { size: 9 } }
          }
        }
      }
    })
  }

  formatSourceLabel(source) {
    const labels = {
      upload: 'Upload',
      camera: 'Camera',
      photo_library: 'Photo Library',
      manual: 'Manual'
    }
    return labels[source] || source
  }

  formatDate(dateStr) {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
}
